import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { supabase } from '../lib/supabase';
import CachedImg from '../components/CachedImg';
import { retrySupabase } from '../utils/retry';
import {
  loadLastCrearAnuncioState,
  mergeLastCrearAnuncioState,
  type CrearAnuncioModeloState,
  type CrearAnuncioObjetivo,
} from '../utils/crearAnuncioStorage';
import { palabraDeModeloActual } from '../lib/nombreDeModelo';

// Tipos para la selección de pose
type PoseOption = { id: string; name: string; image: string };

export const SeleccionarPoseModelo: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsRaw = params.get('ids');
  const ids = useMemo(
    () => {
      if (!idsRaw) return [] as string[];
      return idsRaw.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0');
    },
    [idsRaw]
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'personalizado';
  const composicion = (params.get('composicion') as 'modelo' | 'producto') || 'modelo';
  const tipo = (params.get('tipo') as 'imagen' | 'video') || 'imagen';
  const formato = (params.get('formato') as 'post' | 'story' | 'square') || 'post';
  const cantidadImagenesRaw = params.get('cantidad_imagenes');
  const cantidadImagenes = useMemo(() => {
    const n = Number(cantidadImagenesRaw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [cantidadImagenesRaw]);

  const objetivo = params.get('objetivo') || '';
  const detalles_oferta = params.get('detalles_oferta') || '';

  const estudioVirtual = params.get('estudio_virtual') === '1';

  const poseParam = params.get('pose') || '';

  // Modelo (características) ya seleccionadas en el paso anterior
  const modeloMode = (params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado') || 'ia';
  const modeloGuardadoId = params.get('modelo_guardado_id') || '';
  const modeloGuardadoNombre = params.get('modelo_guardado_nombre') || '';
  const genero = params.get('genero') || '';
  const edad = params.get('edad') || '';
  const tipoCuerpo = params.get('tipo_cuerpo') || '';
  const tonoPiel = params.get('tono_piel') || '';
  const colorPelo = params.get('color_pelo') || '';
  const largoPelo = params.get('largo_pelo') || '';
  const descModelo = params.get('desc_modelo') || '';

  // Estado para poses
  const savedStateRef = useRef(loadLastCrearAnuncioState());
  const savedPose = savedStateRef.current?.pose || '';

  const [poseOptions, setPoseOptions] = useState<PoseOption[]>([]);
  const [loadingPose, setLoadingPose] = useState(true);
  const [pose, setPose] = useState<string>(poseParam || savedPose || '');

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const getDefaultPoseOptions = useCallback((): PoseOption[] => {
    const basePath = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/planos';
    return [
      { id: 'cuerpo-entero', name: 'Plano completo', image: `${basePath}/plano_cuerpocompleto.jpg` },
      { id: 'plano-superior', name: 'Plano superior', image: `${basePath}/plano_superior.jpeg` },
      { id: 'plano-espalda', name: 'Plano espalda', image: `${basePath}/plano_espalda.png` },
      { id: 'plano-sentada', name: 'Plano sentada', image: `${basePath}/plano_sentada.png` },
    ];
  }, []);

  // Cargar poseOptions desde el producto (según model_view)
  useEffect(() => {
    const fetchPoseOptions = async () => {
      try {
        setLoadingPose(true);
        const persistedIds = Array.isArray(savedStateRef.current?.selectedVariantIds)
          ? (savedStateRef.current!.selectedVariantIds as any[]).map((s) => String(s)).map((s) => s.trim()).filter(Boolean)
          : [];
        const variantIds = (ids && ids.length > 0) ? ids : persistedIds;
        if (!variantIds || variantIds.length === 0) {
          const options = getDefaultPoseOptions();
          setPoseOptions(options);
          if (!pose && options.length > 0) setPose(options[1]?.id || options[0].id);
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user found');

        const { data: profile } = await retrySupabase(async () =>
          await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single()
        );

        if (!(profile as any)?.id_company) throw new Error('No company associated with user');

        const firstVariantId = variantIds[0];
        const { data, error } = await retrySupabase(async () =>
          await supabase
            .from('variantes')
            .select(`
              product_id,
              productos!inner (
                id,
                model_view
              )
            `)
            .eq('id', firstVariantId)
            .eq('id_company', (profile as any).id_company)
            .single()
        );

        if (error) throw error;
        const product = (data as any)?.productos;
        const modelView: string | null = product?.model_view ?? null;

        const basePath = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/planos';
        const options: PoseOption[] = [];

        // Plano completo para todos excepto calzado
        options.push({ id: 'cuerpo-entero', name: 'Plano completo', image: `${basePath}/plano_cuerpocompleto.jpg` });

        // Plano superior/inferior según model_view
        if (['torso', 'manos', 'cabeza', 'cuerpo_completo', 'na', null].includes((modelView as any))) {
          options.push({ id: 'plano-superior', name: 'Plano superior', image: `${basePath}/plano_superior.jpeg` });
        } else if (['piernas', 'calzado'].includes(modelView || '')) {
          options.push({ id: 'plano-inferior', name: 'Plano inferior', image: `${basePath}/plano_inferior.png` });
        }

        // Opción de enfoque específica
        let thirdOption: PoseOption | undefined;
        switch (modelView) {
          case 'torso':
          case 'cuerpo_completo':
          case 'na':
          case null:
            thirdOption = { id: 'foco-torso', name: 'Plano enfoque', image: `${basePath}/plano_foco_torso.jpg` };
            break;
          case 'piernas':
            thirdOption = { id: 'foco-pantalon', name: 'Plano enfoque', image: `${basePath}/plano_foco_pantalon.jpg` };
            break;
          case 'calzado':
            thirdOption = { id: 'foco-zapatos', name: 'Plano enfoque', image: `${basePath}/plano_foco_zapatos.png` };
            break;
          case 'manos':
            thirdOption = { id: 'foco-mano', name: 'Plano enfoque', image: `${basePath}/plano_foco_mano.jpeg` };
            break;
          case 'cabeza':
            thirdOption = { id: 'foco-cabeza', name: 'Plano enfoque', image: `${basePath}/plano_foco_cabeza.jpeg` };
            break;
        }
        if (thirdOption) options.push(thirdOption);

        // Add remaining focus options
        if (modelView !== 'calzado') {
          options.push({ id: 'plano-espalda', name: 'Plano espalda', image: `${basePath}/plano_espalda.png` });
        }
        options.push({ id: 'plano-sentada', name: 'Plano sentada', image: `${basePath}/plano_sentada.png` });

        setPoseOptions(options);

        const savedPoseValue = savedStateRef.current?.pose;
        let defaultPose = poseParam || savedPoseValue || '';

        if (!defaultPose || !options.some((opt) => opt.id === defaultPose)) {
          if (['torso', 'manos', 'cabeza', 'cuerpo_completo', 'na', null].includes(modelView as any)) {
            defaultPose = 'plano-superior';
          } else if (modelView === 'calzado') {
            defaultPose = 'foco-zapatos';
          } else if (options.length > 0) {
            defaultPose = options[0].id;
          }
        }

        if (defaultPose) {
          setPose(defaultPose);
        }
      } catch (e) {
        console.error('Error fetching pose options', e);
        const options = getDefaultPoseOptions();
        setPoseOptions(options);
        if (!pose && options.length > 0) setPose(options[1]?.id || options[0].id);
      } finally {
        setLoadingPose(false);
      }
    };

    fetchPoseOptions();
  }, [ids]);

  useEffect(() => {
    const trimmedDetalles = detalles_oferta.trim();
    const allowedObjetivos: CrearAnuncioObjetivo[] = ['mostrar', 'oferta', 'lanzamiento', 'evento'];
    const objetivoValue = allowedObjetivos.includes(objetivo as CrearAnuncioObjetivo)
      ? (objetivo as CrearAnuncioObjetivo)
      : undefined;

    let modeloPayload: CrearAnuncioModeloState | undefined;
    if (pose) {
      modeloPayload = { mode: modeloMode };
      if (modeloMode === 'guardados') {
        if (modeloGuardadoId) modeloPayload.guardadoId = modeloGuardadoId;
        if (modeloGuardadoNombre) modeloPayload.guardadoNombre = modeloGuardadoNombre;
      } else if (modeloMode === 'personalizado') {
        if (genero) modeloPayload.genero = genero;
        if (edad) modeloPayload.edad = edad;
        if (tipoCuerpo) modeloPayload.tipoCuerpo = tipoCuerpo;
        if (tonoPiel) modeloPayload.tonoPiel = tonoPiel;
        if (colorPelo) modeloPayload.colorPelo = colorPelo;
        if (largoPelo) modeloPayload.largoPelo = largoPelo;
        const trimmedDesc = descModelo.trim();
        if (trimmedDesc) modeloPayload.descripcion = trimmedDesc;
      }
    }

    mergeLastCrearAnuncioState({
      modo,
      composicion,
      tipo,
      formato,
      cantidadImagenes: cantidadImagenes ?? undefined,
      objetivo: objetivoValue ?? null,
      detallesOferta: trimmedDetalles ? trimmedDetalles : null,
      pose: pose || null,
      modelo: modeloPayload,
    });
  }, [
    ids,
    modo,
    composicion,
    tipo,
    formato,
    cantidadImagenes,
    objetivo,
    detalles_oferta,
    pose,
    modeloMode,
    modeloGuardadoId,
    modeloGuardadoNombre,
    genero,
    edad,
    tipoCuerpo,
    tonoPiel,
    colorPelo,
    largoPelo,
    descModelo,
  ]);

  const handleContinue = () => {
    const qp = estudioVirtual ? new URLSearchParams(location.search) : new URLSearchParams();
    const persistedIds = Array.isArray(savedStateRef.current?.selectedVariantIds)
      ? (savedStateRef.current!.selectedVariantIds as any[])
          .map((v) => String(v))
          .map((s) => s.trim())
          .filter(Boolean)
          .filter((s) => s !== '0')
      : [];
    const resolvedIds = (ids && ids.length > 0) ? ids : persistedIds;
    if (resolvedIds.length > 0) qp.set('ids', resolvedIds.join(','));
    qp.set('modo', modo);
    qp.set('composicion', composicion);
    qp.set('tipo', tipo);
    qp.set('formato', formato);
    if (cantidadImagenes) qp.set('cantidad_imagenes', String(cantidadImagenes));
    if (objetivo) qp.set('objetivo', objetivo);
    if (detalles_oferta.trim()) qp.set('detalles_oferta', detalles_oferta.trim());

    if (pose) qp.set('pose', pose);
    qp.set('modelo_mode', modeloMode);
    if (modeloMode === 'guardados' && modeloGuardadoId) {
      qp.set('modelo_guardado_id', String(modeloGuardadoId));
      if (modeloGuardadoNombre) qp.set('modelo_guardado_nombre', modeloGuardadoNombre);
    } else if (modeloMode === 'personalizado') {
      if (genero) qp.set('genero', genero);
      if (edad) qp.set('edad', edad);
      if (tipoCuerpo) qp.set('tipo_cuerpo', tipoCuerpo);
      if (tonoPiel) qp.set('tono_piel', tonoPiel);
      if (colorPelo) qp.set('color_pelo', colorPelo);
      if (largoPelo) qp.set('largo_pelo', largoPelo);
      if (descModelo.trim()) qp.set('desc_modelo', descModelo.trim());
    }

    // Destino único: el flujo viejo (elegir-fondo/seleccionar-composicion) se
    // eliminó; en la práctica siempre se llegaba acá con estudio_virtual=1
    // (única entrada: PruebaNuevaInterfaz → PersonalizarModeloEstudioVirtual).
    qp.set('estudio_virtual', '1');
    navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?${qp.toString()}`);
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-content mx-auto min-h-[100dvh] md:min-h-screen bg-surface-mobile sm:bg-transparent">
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">

        {estudioVirtual ? (
          <AppleHeader title={palabraDeModeloActual().titulo} onBack={() => navigate(-1)} actionLabel="Listo" onAction={handleContinue} />
        ) : (
          <AppleHeader title="Seleccioná la pose" />
        )}

        {/* Progress */}
        {!estudioVirtual && (
          <div className="mt-4">
            <div className="w-full h-1 bg-gray-200 rounded-full">
              <div className="h-1 bg-blue-500 rounded-full" style={{ width: '80%' }} />
            </div>
          </div>
        )}

        {/* Pose options */}
        <div className="mt-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Seleccioná la pose</h2>
          <p className="text-sm text-gray-600 mt-1">Según el tipo de producto, te sugerimos los planos más adecuados.</p>
          {loadingPose ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-44 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : poseOptions.length === 0 ? (
            <div className="text-sm text-gray-600 mt-3">No hay opciones de pose sugeridas para este producto. Podés continuar sin seleccionar.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3">
              {poseOptions.map((opt) => {
                const selected = pose === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPose(opt.id)}
                    className={`w-full text-left border ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                  >
                    <div className="relative overflow-hidden rounded-xl">
                      <CachedImg src={opt.image} alt={opt.name} className="w-full h-40 object-cover" />
                      {selected && (
                        <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                          <Check className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 mt-2 font-semibold">{opt.name}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer actions (desktop) */}
        {!estudioVirtual && (
          <div className="mt-6 hidden sm:flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>

            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm bg-blue-500 hover:bg-blue-600 text-white`}
              onClick={handleContinue}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile spacer for fixed bottom bar */}
      {!estudioVirtual && (
        <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />
      )}

      {/* Mobile bottom bar */}
      {!estudioVirtual && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
          <div className="max-w-content mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm bg-blue-500 hover:bg-blue-600 text-white`}
              onClick={handleContinue}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeleccionarPoseModelo;
